import React from 'react';
import { useTranslation } from 'react-i18next';
import { ReactComponent as IconMessage } from '../../../../../assets/images/icons/icon_message.svg';
import { ReactComponent as IconPhone } from '../../../../../assets/images/icons/icon_phone.svg';
import { ReactComponent as IconWarranty } from '../../../../../assets/images/icons/icon_warranty.svg';
import { ReactComponent as IconCalendar } from '../../../../../assets/images/icons/icon_calendar.svg';
import { ReactComponent as IconCellular } from '../../../../../assets/images/icons/icon_cellular.svg';
import styles from '../../../ProductDetails.module.scss';
import ValuePropsBox from '../ValuePropsBox';
import { showNanoVariant } from '../../../../../components/NanoBanner/NanoBanner';
import { useSelector } from 'react-redux';
import * as types from '../../../../../types';
import { formattedCentsToDollars } from '../../../../../lib/util/money';
import { useIsGift } from '../../../../../lib/util/isGift';
import { FiCountry, useLocale } from '../../../../../contexts/LocaleContext';
import BellLogo from '../../../../../assets/images/bell-logo.png';
import RogersLogo from '../../../../../assets/images/rogers-logo.png';
import TelusLogo from '../../../../../assets/images/telus-logo.png';
import O2Logo from '../../../../../assets/images/o2-logo.svg';
import { isEnrolledInPositionDiscount } from '../../../../../lib/positionDiscount';

interface MembershipValuePropsProps {
  inAppManagementVariant?: boolean;
  redemptionType?: types.RedemptionType;
  preselectedSubscription?: types.ISubscriptionProduct;
  isMini?: boolean;
  freeTrialEnabled?: boolean;
}

export default function PlusMembershipValueProps({
  inAppManagementVariant = false,
  redemptionType,
  preselectedSubscription,
  freeTrialEnabled,
}: MembershipValuePropsProps) {
  const { t } = useTranslation();
  const { country } = useLocale();
  const nanoVariantEnabled = showNanoVariant();
  const multiDogHouseholdDiscountEnabled = useSelector(
    (state: types.AppState) => state.config.siteConfig.multiDogHouseholdDiscountEnabled ?? false,
  );
  const gwpFreeTrialMonths = useSelector((state: types.AppState) => state.config.siteConfig.gwpFreeTrialMonths);
  const isGift = useIsGift();
  const enrolledInPositionDiscount = isEnrolledInPositionDiscount();

  return (
    <div className={styles.valuePropsSection}>
      {!inAppManagementVariant &&
        multiDogHouseholdDiscountEnabled &&
        redemptionType !== 'gwp_free_trial' &&
        !nanoVariantEnabled &&
        !isGift && (
          <div className={styles.multipleDogsBox}>
            <span className={styles.multipleDogsHeader}>{t('multiple_pets')} </span>
            <span className={styles.multipleDogsDescription}>
              {enrolledInPositionDiscount
                ? t('get_up_to_15_percent_off_on_additional_memberships')
                : t('get_5_percent_off_all_of_your_fi_memberships')}
            </span>
          </div>
        )}
      {!inAppManagementVariant &&
        redemptionType === 'gwp_free_trial' &&
        gwpFreeTrialMonths &&
        preselectedSubscription && (
          <div className={styles.gwpFreeTrialBox}>
            <div className={styles.gwpFreeTrialDescription}>
              {t('after_months_free_months_renews_at_price', {
                months: gwpFreeTrialMonths,
                price: formattedCentsToDollars(preselectedSubscription.priceInCents),
              })}{' '}
              {t('mo_membership', { months: preselectedSubscription.renewalMonths })}
            </div>
          </div>
        )}

      <ValuePropsBox
        header={undefined}
        valueProps={[
          ...(freeTrialEnabled
            ? []
            : [
                {
                  icon: <IconCalendar />,
                  text: (
                    <div className={styles.valueProp}>
                      <span className={styles.valuePropHeaderBold}>{t('thirty_day_risk_free_trial')}</span>
                    </div>
                  ),
                },
              ]),
          {
            icon: <IconWarranty />,
            text: (
              <div className={styles.valueProp}>
                <span className={styles.valuePropHeader}>
                  <span className={styles.valuePropHeaderBold}>{t('one_year_warranty')}</span>
                </span>
              </div>
            ),
          },
          {
            icon: <IconMessage />,
            text: (
              <div className={styles.valueProp}>
                <span className={styles.valuePropHeader}>
                  <span className={styles.valuePropHeaderBold}>
                    {t('online_support')} {t('7_days_week_on_chat_or_email_to_answer_any_questions')}
                  </span>
                </span>
              </div>
            ),
          },
          ...(country !== FiCountry.GB
            ? [
                {
                  icon: <IconPhone />,
                  text: (
                    <div className={styles.valueProp}>
                      <span className={styles.valuePropHeader}>
                        <span className={styles.valuePropHeaderBold}>
                          {t('phone_support')}{' '}
                          {t('talk_to_a_human_7_days_a_week_10am_8pm_et_call_us_at_1_888_4_fi_dogs')}
                        </span>
                      </span>
                    </div>
                  ),
                },
              ]
            : []),
          ...(country === FiCountry.CA
            ? [
                {
                  icon: <IconCellular />,
                  text: (
                    <div className={styles.valueProp}>
                      <span className={styles.valuePropHeaderBold}>{t('canadian_cellular_supported')}</span>
                      <div className={styles.carrierLogos}>
                        <img src={BellLogo} alt="Bell" />
                        <img src={RogersLogo} alt="Rogers" />
                        <img src={TelusLogo} alt="Telus" />
                      </div>
                    </div>
                  ),
                },
              ]
            : []),
          ...(country === FiCountry.GB
            ? [
                {
                  icon: <IconCellular />,
                  text: (
                    <div className={styles.valueProp}>
                      <span className={styles.valuePropHeaderBold}>{t('british_cellular_supported')}</span>
                      <div className={styles.carrierLogos}>
                        <img src={O2Logo} alt="O2" />
                      </div>
                    </div>
                  ),
                },
              ]
            : []),
        ]}
      />
    </div>
  );
}
